import React, { useState, useCallback, useMemo, useContext } from "react";
import { Container, Card, CardBody, CardHeader } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb/BreadCrumb";
import TableContainerMin from "../../../Components/Common/TableContainerMin";
import { useQuery, useQueryClient } from "react-query";
import { DeleteOutlined, DownloadOutlined, EyeOutlined, LinkOutlined } from "@ant-design/icons";
import { Button, Modal, Spin, Tag } from "antd";
import HistoryContext from "../../../Components/Hooks/HistoryContext";
import toast from "react-hot-toast";
import { deleteMaterial, getDemandeMateriels, getVehicleHistory } from "../services/materielApi";
import "../../Styles/style.css";
import { getUserById } from "../../Users/services/userApi";
import "../utils/materiels.css";
import AddEditMaterielForm from "../component/AddEditMaterielsForm";
import AffectDemandeForm from "../component/AffectDemandeToInterv";
import { formatDate } from "../../../Components/Common/FormatDate/FormatDate";
import DetailDemande from "../component/DetailDemande";
import { formatCriticity, formatState, getCriticityColor, getStateColor } from "../../../Components/Common/FormatDate/formatStatus";
import DataExport from "../../Exportcsv/DataExport";
import { getDateRangeTitle } from "../../../Components/Common/Date/triDate";


const DemandeMateriel = () => {
  const history = useContext(HistoryContext);
  const queryClient = useQueryClient();

  document.title = `Demandes | ${process.env.REACT_APP_NAME} - ${process.env.REACT_APP_DESCRIPTION}`;

  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [isAffectModalOpen, setIsAffectModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [driverName, setDriverName] = useState(null);
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [selectedVehHistory, setSelectedVehHistory] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteMatName, setDeleteMatName] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const { isLoading, isError, error, data: demandeMaterials } = useQuery("demandes", getDemandeMateriels);

  const toggleAddEditModal = useCallback(
    (vehicule = null) => {
      setSelectedDemande(vehicule);
      setIsAddEditModalOpen((prev) => !prev);
      if (isAffectModalOpen) setIsAffectModalOpen(false);
    },
    [isAffectModalOpen]
  );

  const handleEdit = (vehicule) => {
    toggleAddEditModal(vehicule);
  };

  const toggleHistoryModal = useCallback(
    async (vehicule = null) => {
      setSelectedDemande(vehicule);
      try {
        const oldDriver = await getVehicleHistory(vehicule?.id);
        setSelectedVehHistory(oldDriver);
      } catch (error) {
        console.error("Error fetching material history:", error);
      }
      setIsHistoryModalOpen((prev) => !prev);
      if (isAddEditModalOpen) setIsAddEditModalOpen(false);
      if (isAffectModalOpen) setIsAffectModalOpen(false);
    },
    [isAddEditModalOpen, isAffectModalOpen]
  );

  const handleHistory = (vehicule) => {
    toggleHistoryModal(vehicule);
  };

  const toggleAffectModal = useCallback(
    (vehicule = null) => {
      setSelectedDemande(vehicule);
      setIsAffectModalOpen((prev) => !prev);
      if (isAddEditModalOpen) setIsAddEditModalOpen(false);
    },
    [isAddEditModalOpen]
  );

  const handleAffect = async (vehicule) => {
    try {
      const selectedDriverVeh = await getUserById(vehicule?.actualUserId);
      setDriverName(selectedDriverVeh?.name || "Pas de chauffeur");
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
    toggleAffectModal(vehicule);
  };

  const handleDelete = async (matId) => {
    setIsDeleting(true);
    try {
      await deleteMaterial(matId);
      queryClient.invalidateQueries("materiels");
      toast.success("Le matériel a été supprimé avec succès 🎉");
    } catch (error) {
      console.error("Error deleting vehicule:", error);
      toast.error(`Erreur lors de la suppression du matériel`);
    } finally {
      setIsDeleting(false);
      setDeleteConfirmModal(false);
    }
  };

  const openDeleteConfirmModal = (matId) => {
    const matToDelete = demandeMaterials.find((mat) => mat.id === matId);
    if (matToDelete) {
      setDeleteTargetId(matId);
      setDeleteMatName(matToDelete.name);
      setDeleteConfirmModal(true);
    }
  };

  const closeDeleteConfirmModal = () => {
    setDeleteConfirmModal(false);
  };

  const columns = useMemo(
    () => [      
      { Header: "Numero", accessor: "requestNumber", filterable: false },
      { Header: "Demande", accessor: "name", filterable: false },
      { Header: "Technicien", 
        accessor: "techName",
        Cell: ({ value }) => value || "-",
        filterable: false,
      },
      // { Header: "Technicien",
      //   accessor: "technician",
      //   Cell: ({ value }) => value ? `${value?.name || ''} ${value?.phone || ''}`.trim() : "-",
      //   filterable: false 
      // },
      { 
        Header: "Date", 
        accessor: "createdAt",
        Cell: ({ value }) => formatDate(value, true),
        filterable: false,        
      },
      // {
      //   Header: "Criticité",
      //   accessor: "level",
      //   Cell: ({ value }) => (
      //     <span
      //       className={`badge bg-${getCriticityColor(value)}-subtle text-${getCriticityColor(value)}`}
      //     >
      //       {formatCriticity(value)}
      //     </span>
      //   ),
      //   filterable: false,
      // },
      {
        Header: "Statut",
        accessor: "status",
        Cell: ({ value }) => (
          <span
            className={`badge bg-${getStateColor(value)}-subtle text-${getStateColor(value)}`}
          >
            {formatState(value)}
          </span>
        ),
        filterable: false,
      },
      {
          Header: "Actions",
          Cell: ({ row }) => (
            <div className="d-flex gap-2">
              <EyeOutlined
                title="Détail"
                size="sm"
                onClick={() => handleHistory(row.original)}
                className="btn btn-soft-primary"
              />
              <LinkOutlined title="Affecter à un intervent" size="sm" onClick={() => handleAffect(row.original)} className="btn btn-soft-success" />

              {/* <CheckOutlined title="Cloturer la demande" size="sm" onClick={() => openConfirmModal(row.original)} className="btn btn-soft-success"/> */}
              
              {/* <EditOutlined
                title="Modifier Materiel"
                size="sm"
                onClick={() => handleEdit(row.original)}
                className="btn btn-soft-warning"
              /> */}

              {/* <DeleteOutlined title="Supprimer matériel" size="sm" onClick={() => openDeleteConfirmModal(row.original.id)} className="btn btn-soft-danger" /> */}
            </div>
          ),
          filterable: false,
      },
    ],
    [handleEdit, openDeleteConfirmModal, handleAffect, handleHistory]
  );


  const tabs = ["Tous", "En service", "Hors service", "Archivés"];
  const [activeTab, setActiveTab] = useState("Tous");

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const filteredStocke = useMemo(() => {
    if (!demandeMaterials) return error?.message || [];
    switch (activeTab) {
      case "En service":
        return demandeMaterials.filter((v) => v.statut === "En service");
      case "Hors service":
        return demandeMaterials.filter((v) => v.statut === "Hors service");
      case "Archivés":
        return demandeMaterials.filter((v) => v.statut === "Archivés");
      default:
        return demandeMaterials;
    }
  }, [demandeMaterials, activeTab]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb page="Demandes" history={history} />
          <Card id="contactList">
            <CardHeader>
              <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="text-title mb-4">
                  Demandes de matériels{" "} <Tag color="#5ea8adff" style={{ fontSize: "12px" }}>{filteredStocke?.length || 0}</Tag>
                </div>

                <DataExport
                  title={getDateRangeTitle(filteredStocke, "createdAt", "Liste des demandes de materiels du")}
                  fileName="Export_Demande_materiels.xlsx"
                  columns={columns}
                  data={filteredStocke}
                  buttonLabel="Exporter"
                  buttonType="primary"
                  buttonClass="active-tab"
                  icon={<DownloadOutlined />}
                />
              </div>
            </CardHeader>
            <CardBody className="pt-0">
              <TableContainerMin
                columns={columns}
                data={filteredStocke}
                isGlobalSearch={true}
                isGlobalFilter={true}
                customPageSize={10}
                className="custom-header-css"
                divClass="table-responsive table-card mb-3"
                tableClass="align-middle table-nowrap"
                theadClass="table-light"
              />
              {isLoading && !isError && (
                <div className="text-center">
                  Chargement, Merci de patienter ... <Spin />
                </div>
              )}
              {filteredStocke && filteredStocke.length === 0 && !isLoading && (
                <div className="text-center">
                  Aucun matériel à afficher pour le moment.
                </div>
              )}
            </CardBody>
          </Card>

          <AddEditMaterielForm
            modal={isAddEditModalOpen}
            setModal={setIsAddEditModalOpen}
            toggle={toggleAddEditModal}
            selectedDemande={selectedDemande}
          />

          <AffectDemandeForm
            modal={isAffectModalOpen}
            setModal={setIsAffectModalOpen}
            toggle={toggleAffectModal}
            selectedDemande={selectedDemande}
            actualDriver={driverName}
          />
          
          <DetailDemande
            modal={isHistoryModalOpen}
            setModal={setIsHistoryModalOpen}
            toggle={toggleHistoryModal}
            selectedDemande={selectedDemande}
            selectedVehHistory={selectedVehHistory}
            isLoading = {isLoading}
            isError={isError} 
            error ={error}
          />

          <Modal
            title={null}
            open={deleteConfirmModal}
            onCancel={closeDeleteConfirmModal}
            footer={null}
            centered
            className="custom-delete-modal"
          >
            <div className="text-center p-3">
              <DeleteOutlined className="mb-3" style={{ fontSize: "48px", color: "#ff4d4f" }} />

              <h4 style={{ fontWeight: "600", marginBottom: "20px" }}>
                Confirmer la suppression
              </h4>

              <p className="text-muted mt-2 mb-2">
                Êtes-vous sûr de vouloir supprimer le matériel{" "}
                <Tag color="red">{deleteMatName}</Tag> ?
              </p>

              <div className="d-flex justify-content-center gap-3 mt-4">
                <Button onClick={closeDeleteConfirmModal} size="middle">
                  Annuler
                </Button>
                <Button
                  type="primary"
                  danger
                  loading={isDeleting}
                  onClick={() => handleDelete(deleteTargetId)}
                >
                  {isDeleting ? "Suppression..." : "Oui, Supprimer !"}
                </Button>
              </div>
            </div>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DemandeMateriel;
